'use client'

import React, { useState, useEffect } from 'react'
import { Menu, X } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import { createPortal } from 'react-dom'

import type { Header as HeaderType } from '@/payload-types'

import { CMSLink } from '@/components/Link'

export const HeaderNav: React.FC<{ data: HeaderType }> = ({ data }) => {
  const navItems = data?.navItems || []
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [hoveredItem, setHoveredItem] = useState<number | null>(null)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const handleItemHover = (index: number) => {
    setHoveredItem(index)
  }

  const handleItemLeave = () => {
    setHoveredItem(null)
  }

  return (
    <>
      {/* Burger Menu Button - Always visible */}
      <button
        className="flex h-10 w-10 items-center justify-center rounded-lg"
        onClick={toggleMenu}
        aria-label="Toggle navigation menu"
        aria-expanded={isMenuOpen}
      >
        <Menu className="h-10 w-10 transition-colors hover:text-accent" />
      </button>

      {/* Full Viewport Navigation Overlay - Portaled */}
      {isMounted &&
        createPortal(
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                className="z-9999 fixed inset-0 flex bg-custom-card-background"
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{
                  type: 'tween',
                  duration: 0.3,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                onClick={() => setIsMenuOpen(false)}
              >
                {/* Left Side - Navigation Menu */}
                <div
                  className="relative flex flex-1 flex-col justify-center pl-16 pr-8"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Vertical Line Separator */}
                  <div className="absolute bottom-0 right-0 top-0 flex w-px flex-col items-center justify-center">
                    <div className="h-2/3 w-[1px] bg-white" />
                  </div>

                  {/* Navigation Items */}
                  <motion.nav
                    className="flex flex-col gap-8"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: 0.1,
                      duration: 0.3,
                    }}
                  >
                    {navItems.map(({ link, group, disableMainLink }, i) => {
                      const hasSubLinks = group?.links && group.links.length > 0
                      const isHovered = hoveredItem === i
                      const shouldDisableMainLink = disableMainLink && hasSubLinks

                      return (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            delay: 0.2 + i * 0.1,
                            duration: 0.3,
                          }}
                        >
                          {shouldDisableMainLink ? (
                            <div className="relative w-max" onMouseEnter={() => handleItemHover(i)}>
                              <span className="!underline-none hover:!underline-none !decoration-none hover:!decoration-none !px-0 pb-2 !font-clash text-6xl font-semibold uppercase text-white transition-colors cursor-default">
                                {link.label}
                              </span>
                              {/* Animated Underline */}
                              <motion.div
                                className="absolute bottom-0 left-0 h-1 bg-accent"
                                initial={{ width: 0 }}
                                animate={{
                                  width: isHovered ? '100%' : 0,
                                  opacity: isHovered ? 1 : 0,
                                }}
                                transition={{
                                  duration: 0.3,
                                  ease: [0.25, 0.46, 0.45, 0.94],
                                }}
                              />
                            </div>
                          ) : (
                            <button
                              className="relative w-min"
                              onMouseEnter={() => handleItemHover(i)}
                            >
                              <CMSLink
                                {...link}
                                appearance="link"
                                className={`!underline-none hover:!underline-none !decoration-none hover:!decoration-none w-max !px-0 pb-2 !font-clash text-6xl font-semibold uppercase text-white transition-colors`}
                                onClick={() => setIsMenuOpen(false)}
                              />
                              {/* Animated Underline */}
                              <motion.div
                                className="absolute bottom-0 left-0 h-1 bg-accent"
                                initial={{ width: 0 }}
                                animate={{
                                  width: isHovered ? '100%' : 0,
                                  opacity: isHovered ? 1 : 0,
                                }}
                                transition={{
                                  duration: 0.3,
                                  ease: [0.25, 0.46, 0.45, 0.94],
                                }}
                              />
                            </button>
                          )}
                        </motion.div>
                      )
                    })}
                  </motion.nav>
                </div>

                {/* Right Side - Sub Navigation */}
                <div
                  className="relative flex flex-1 flex-col justify-center pl-8 pr-16"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AnimatePresence mode="wait">
                    {hoveredItem !== null &&
                      navItems[hoveredItem]?.group?.links &&
                      navItems[hoveredItem].group.links.length > 0 && (
                        <motion.div
                          key={hoveredItem}
                          className="flex flex-col gap-4"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{
                            duration: 0.2,
                          }}
                        >
                          {navItems[hoveredItem].group.links.map((subLink, subIndex) => (
                            <motion.div
                              key={subIndex}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                delay: subIndex * 0.05,
                                duration: 0.2,
                              }}
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <CMSLink
                                {...subLink.link}
                                appearance="link"
                                className="text-2xl text-gray-300 transition-colors hover:text-accent"
                              />
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                  </AnimatePresence>
                </div>

                {/* Close Button - Top Right */}
                <div className="absolute right-4 top-4" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="flex h-12 w-12 items-center justify-center rounded-lg"
                    onClick={toggleMenu}
                    aria-label="Close navigation menu"
                  >
                    <X className="h-8 w-8 text-white transition-colors hover:text-accent" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  )
}
